import { CATEGORIES, PRODUCT } from 'config/api-routes';
import { Category } from 'types/category.types';
import { HomeFeedResponse, ISubTotals, ISubTotalsParams, ProductAiPrefillData, ProductListFilter } from 'types/product.types';
import { DELETE, GET, PATCH, POST } from 'utils/request';
import { formatResponse } from 'utils/service-helpers';

export default class ProductService {
  lastProductSearch = {};

  async getFreeProducts(queryParams = {}, opts = {}) {
    const response = await GET(
      PRODUCT.LIST.GET({
        price: 0,
        sortBy: 'createdAt',
        sortDir: 'desc',
        status: ['reserved', 'published', 'sold'],
        pinnedFirst: 1,
        ...queryParams,
      }),
      opts,
    );

    return formatResponse(response);
  }

  async getFeaturedProducts(queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(PRODUCT.FEATURED.GET(queryParams), opts);

    return formatResponse(response);
  }

  async createProduct(data, opts = {}) {
    const response = await POST(PRODUCT.CREATE.POST(), data, opts);

    return formatResponse(response);
  }

  async removeProduct(slug: string, opts = {}) {
    const response = await DELETE(PRODUCT.REMOVE.DELETE(slug), undefined, opts);

    return formatResponse(response);
  }

  async updateProductBySlug(slug: string, data, opts = {}) {
    const response = await PATCH(PRODUCT.UPDATE.BY_SLUG.PATCH(slug), data, opts);

    return formatResponse(response);
  }

  async getProductUploadUrl(slug: string, type = 'jpeg', opts = {}) {
    const response = await GET(
      PRODUCT.UPLOAD_URL.GET({
        slug,
        type,
        // Random query param to avoid GET call cache on Safari
        timestamp: Math.ceil(Math.random() * Math.random() * 1000000),
      }),
      opts,
    );

    return formatResponse(response);
  }

  async updateProductImage(productId: number, fileName: string, opts = {}) {
    const response = await POST(PRODUCT.IMAGE.UPLOAD.POST(productId), { fileName }, opts);

    return formatResponse(response);
  }

  async updateProductImages(productId: number, images: Array<{ fileName: string; blurhash?: string }>, opts = {}) {
    const response = await POST(PRODUCT.IMAGES.UPLOAD.POST(productId), { images }, opts);

    return formatResponse(response);
  }

  async postImageToGoogle(URI, imageFile) {
    return new Promise((resolve, reject) => {
      const file = imageFile;

      // Uses xhr, since axios is unable to post raw data
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', URI, true);
      xhr.onload = () => {
        const status = xhr.status;

        if (status === 200) {
          return resolve(true);
        }

        console.error('Image upload failed');

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject(false);
      };

      xhr.onerror = (err) => {
        console.error(err, 'Image upload failed');

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject(false);
      };
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(imageFile);
    });
  }

  async removeProductImage(productId: number, imageId: number, opts = {}) {
    const response = await DELETE(PRODUCT.IMAGE.REMOVE.DELETE(productId, imageId), undefined, opts);

    return formatResponse(response);
  }

  async updateProduct(productId: number, productData: any, opts = {}) {
    const response = await PATCH(PRODUCT.UPDATE.PATCH(productId), productData, opts);

    return formatResponse(response);
  }

  async likeProduct(
    productId: number,
    origin: { searchGuid?: string; recommendationId?: string; recommendationEngine?: string } = {},
    opts = {},
  ) {
    const { searchGuid, recommendationId, recommendationEngine } = origin;
    const body = searchGuid
      ? { searchGuid }
      : recommendationId || recommendationEngine
        ? { ...(recommendationId && { recommendationId }), ...(recommendationEngine && { recommendationEngine }) }
        : undefined;
    const response = await POST(PRODUCT.LIKE.CREATE.POST(productId), body, opts);

    return formatResponse(response);
  }

  async unlikeProduct(
    productId: number,
    origin: { searchGuid?: string; recommendationId?: string; recommendationEngine?: string } = {},
    opts = {},
  ) {
    const { searchGuid, recommendationId, recommendationEngine } = origin;
    const queryParams = searchGuid
      ? { searchGuid }
      : recommendationId || recommendationEngine
        ? { ...(recommendationId && { recommendationId }), ...(recommendationEngine && { recommendationEngine }) }
        : undefined;
    const response = await DELETE(PRODUCT.LIKE.REMOVE.DELETE(productId, queryParams), undefined, opts);

    return formatResponse(response);
  }

  async getLikedProducts(opts = {}) {
    const response = await GET(PRODUCT.LIKE.LIST.GET(), opts);

    return formatResponse(response);
  }

  async getAvailableTags(opts = {}) {
    const response = await GET(PRODUCT.TAG.GET(), opts);

    return formatResponse(response);
  }

  async getAvailableSizes(categoryId?: number, queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(PRODUCT.SIZE.GET(categoryId, queryParams), opts);

    return formatResponse(response);
  }

  async getProductFilterSizes(categoryId?: number, queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(PRODUCT.SIZE.FILTER.GET(categoryId, queryParams), opts);

    return formatResponse(response);
  }

  async getProducts(queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(PRODUCT.LIST.GET(queryParams), opts, queryParams?.shopId ? undefined : this.postSearchMetrics);

    return formatResponse(response);
  }

  async getHomeFeed(
    queryParams?: { offset: number; limit: number; firstProductId?: number | string | null },
    opts = {},
  ): Promise<HomeFeedResponse> {
    const response = await GET(PRODUCT.HOME_FEED.GET(queryParams), opts);

    return formatResponse(response);
  }

  async getProductsByTag(tag?: string, queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(
      PRODUCT.LIST.GET({
        tags: tag && decodeURI(tag),
        sortBy: 'createdAt',
        sortDir: 'desc',
        status: ['published', 'reserved', 'sold'],
        pinnedFirst: 1,
        ...queryParams,
      }),
      opts,
      this.postSearchMetrics,
    );

    return formatResponse(response);
  }

  async getRecommendations(productSlug: string, lastListedAt?: string, opts = {}) {
    const queryParams: {
      lastListedAt?: string;
    } = {};

    if (lastListedAt) {
      queryParams.lastListedAt = lastListedAt;
    }

    const response = await GET(PRODUCT.RECOMMENDATION.GET(productSlug, queryParams), opts);

    return formatResponse(response);
  }

  async getProductsByBrandId(brandId?: number, queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(
      PRODUCT.LIST.GET({
        brandId,
        sortBy: 'createdAt',
        sortDir: 'desc',
        status: ['published', 'reserved', 'sold'],
        pinnedFirst: 1,
        ...queryParams,
      }),
      opts,
    );

    return formatResponse(response);
  }

  async getLikedObjects(type: string, queryParams?, opts = {}) {
    const response = await GET(
      PRODUCT.LIKE.LIST.GET({
        sortBy: 'createdAt',
        sortDir: 'desc',
        status: ['published', 'reserved'],
        numberOfProducts: 3,
        type,
        ...queryParams,
      }),
      opts,
    );

    return formatResponse(response);
  }

  async getProductBySlug(slug: string, queryParams = {}, opts = {}) {
    const response = await GET(PRODUCT.ONE.GET(slug, queryParams), opts);

    return formatResponse(response);
  }

  async getProductById(id: number, queryParams = {}, opts = {}) {
    const response = await GET(PRODUCT.ONE.GET(id, queryParams), opts);

    return formatResponse(response);
  }

  async getBundlingAvaliability(slug: string, queryParams = {}, opts = {}) {
    const response = await GET(PRODUCT.ONE.GET_BUNDLING_AVALIABILITY(slug, queryParams), opts);

    return formatResponse(response);
  }

  async searchProducts(search: string, queryParams?: Record<string, any>, opts = {}) {
    const response = await GET(
      PRODUCT.SEARCH.GET({
        query: search,
        ...queryParams,
      }),
      opts,
      queryParams?.getFilterCategories
        ? undefined
        : (metricsData) => {
            this.postSearchMetrics({
              ...metricsData,
              mode: queryParams?.mode,
            });
          },
    );

    const productSearch = search + JSON.stringify(queryParams || {});

    if ((!this.lastProductSearch || productSearch !== this.lastProductSearch) && !queryParams?.getFilterData && queryParams?.offset === 0) {
      this.lastProductSearch = productSearch;
    }

    return formatResponse(response);
  }

  async getCampaignsProducts(opts = {}) {
    const response = await GET(PRODUCT.CAMPAIGNS.GET(), opts);

    return formatResponse(response);
  }

  async getCategories(queryParams?, opts = {}): Promise<Category[]> {
    const response = await GET(CATEGORIES.LIST.GET(queryParams), opts);

    return formatResponse(response);
  }

  async getHomepageProductListFilters(opts = {}): Promise<ProductListFilter[]> {
    const response = await GET(PRODUCT.FILTER.GET_HOMEPAGE(), opts);

    const data = formatResponse(response);

    return data?.list || [];
  }

  async pinningProduct(productId) {
    const response = await POST(PRODUCT.ADD_PIN.POST(productId), {}, {});

    return formatResponse(response);
  }

  async removingProductPin(productId) {
    const response = await DELETE(PRODUCT.REMOVE_PIN.DELETE(productId), {}, {});

    return formatResponse(response);
  }

  async hideProduct(productId, body) {
    const response = await POST(PRODUCT.HIDE_PRODUCT.POST(productId), body, {});

    return formatResponse(response);
  }

  async deleteProduct(productId, param = {}) {
    const response = await DELETE(PRODUCT.DELETE_PRODUCT.DELETE(productId), param, {});

    return formatResponse(response);
  }

  async getOrderSubTotals({ productId, discountCode: discountCode }: ISubTotalsParams, opts = {}): Promise<ISubTotals> {
    const response = await GET(PRODUCT.ORDER_SUBTOTALS.GET(productId, discountCode), opts);

    return formatResponse(response);
  }

  async getDeleteReasons(opts = {}) {
    const response = await GET(PRODUCT.DELETE_REASONS.GET(), opts);

    return formatResponse(response);
  }

  async getHideReasons(opts = {}) {
    const response = await GET(PRODUCT.HIDE_REASONS.GET(), opts);

    return formatResponse(response);
  }

  async postSearchMetrics(data: { searchEngine: string; searchTimeMs: number; mode?: string }) {
    if (typeof window === 'undefined') {
      return;
    }

    const response = await POST(
      PRODUCT.SEARCH_METRICS.POST(),
      { ...data, pageUrl: window.location.href, device: 'web' },
      { silentFail: true },
    );

    return formatResponse(response);
  }

  async getAiPrefillData(
    data: {
      productId: number;
      images: string[];
    },
    opts = {},
  ): Promise<ProductAiPrefillData> {
    const response = await POST(PRODUCT.AI_PREFILL.POST(), data, opts);

    return formatResponse(response);
  }
}
